import {FieldPath} from 'react-hook-form';

import {prop, propOr} from 'ramda';

import i18n from '@dms/i18n';

import {BaseVehicle} from '../../core/types/BaseVehicle';
import {Field} from './primitives/Field';

type FieldMap = Partial<Record<FieldPath<BaseVehicle>, string>>;

const getSimpleNumericFieldsMap = (i18nInstance: typeof i18n) =>
  ({
    power: i18nInstance.t('entity.vehicle.labels.power'),
    maximalPower: i18nInstance.t('entity.vehicle.labels.maximalPower'),
    engineVolume: i18nInstance.t('entity.vehicle.labels.engineCapacity'),
    cylinderCount: i18nInstance.t('entity.vehicle.labels.numberOfCylinders'),
    gearCount: i18nInstance.t('entity.vehicle.labels.numberOfGears'),
    // NEDC fuel consumption fields
    fuelConsumptionUrban: i18nInstance.t('entity.vehicle.labels.fuelConsumptionUrban'),
    maxTorque: i18nInstance.t('entity.vehicle.labels.maxTorque'),
    fuelConsumptionCombined: i18nInstance.t('entity.vehicle.labels.fuelConsumptionCombined'),
    fuelConsumptionExtraUrban: i18nInstance.t('entity.vehicle.labels.fuelConsumptionExtraUrban'),
    // WLTP fuel consumption fields (liquid/gas)
    fuelConsumptionWltpCombined: i18nInstance.t(
      'entity.vehicle.labels.fuelConsumptionWltpCombined'
    ),
    fuelConsumptionWltpCityCenter: i18nInstance.t(
      'entity.vehicle.labels.fuelConsumptionWltpCityCenter'
    ),
    fuelConsumptionWltpOutskirts: i18nInstance.t(
      'entity.vehicle.labels.fuelConsumptionWltpOutskirts'
    ),
    fuelConsumptionWltpCountryRoads: i18nInstance.t(
      'entity.vehicle.labels.fuelConsumptionWltpCountryRoads'
    ),
    fuelConsumptionWltpHighway: i18nInstance.t('entity.vehicle.labels.fuelConsumptionWltpHighway'),
    fuelConsumptionWltpWeighted: i18nInstance.t(
      'entity.vehicle.labels.fuelConsumptionWltpWeighted'
    ),
    // WLTP consumption fields (electric)
    fuelConsumptionWltpCombinedKwh: i18nInstance.t(
      'entity.vehicle.labels.fuelConsumptionWltpCombined'
    ),
    fuelConsumptionWltpCityCenterKwh: i18nInstance.t(
      'entity.vehicle.labels.fuelConsumptionWltpCityCenter'
    ),
    fuelConsumptionWltpOutskirtsKwh: i18nInstance.t(
      'entity.vehicle.labels.fuelConsumptionWltpOutskirts'
    ),
    // WLTP only fuel consumption fields (electric)
    fuelConsumptionWltpCountryRoadsKwh: i18nInstance.t(
      'entity.vehicle.labels.fuelConsumptionWltpCountryRoads'
    ),
    fuelConsumptionWltpHighwayKwh: i18nInstance.t(
      'entity.vehicle.labels.fuelConsumptionWltpHighway'
    ),
    fuelConsumptionWltpWeightedCombinedKwh: i18nInstance.t(
      'entity.vehicle.labels.fuelConsumptionWltpWeightedCombinedKwh'
    ),
    // CO2 emission fields
    carbonDioxideEmission: i18nInstance.t('entity.vehicle.labels.emissionsCo2WO'),
    carbonDioxideEmissionDischarged: i18nInstance.t(
      'entity.vehicle.labels.carbonDioxideEmissionDischarged'
    ),
    carbonDioxideEmissionWeightedCombined: i18nInstance.t(
      'entity.vehicle.labels.carbonDioxideEmissionWeightedCombined'
    ),
    'dimensions.width': i18nInstance.t('general.labels.width'),
    'dimensions.height': i18nInstance.t('general.labels.height'),
    'dimensions.length': i18nInstance.t('general.labels.length'),
    operatingWeight: i18nInstance.t('entity.vehicle.labels.operatingWeight'),
    weight: i18nInstance.t('entity.vehicle.labels.maxWeight'),
    maximalLoadCapacity: i18nInstance.t('entity.vehicle.labels.maxLoadCapacity'),
    loadingCapacity: i18nInstance.t('entity.vehicle.labels.loadCapacity'),
    trunkSpace: i18nInstance.t('entity.vehicle.labels.trunkSpace'),
    wheelBase: i18nInstance.t('entity.vehicle.labels.wheelBase'),
    ownerCount: i18nInstance.t('entity.vehicle.labels.previousOwners'),
    primaryKeyCount: i18nInstance.t('entity.vehicle.labels.numberOfKeysFull'),
    lastServiceMileage: i18nInstance.t('entity.vehicle.labels.lastService'),
    nextServiceMileage: i18nInstance.t('entity.vehicle.labels.nextService'),
    mileage: i18nInstance.t('entity.vehicle.labels.mileage'),
    warrantyMileage: i18nInstance.t('entity.vehicle.labels.warrantyMileageLimit'),
    serviceContractMileageLimit: i18nInstance.t(
      'entity.vehicle.labels.serviceContractMileageLimit'
    ),
    europalletStorageSpaces: i18nInstance.t('entity.vehicle.labels.europalletStorageSpaces'),
    'dimensionsLoadingSpace.length': i18nInstance.t('entity.vehicle.labels.loadSpaceLength'),
    'dimensionsLoadingSpace.width': i18nInstance.t('entity.vehicle.labels.loadSpaceWidth'),
    'dimensionsLoadingSpace.height': i18nInstance.t('entity.vehicle.labels.loadSpaceHeight'),
    loadingArea: i18nInstance.t('entity.vehicle.labels.loadArea'),
    batteryCondition: i18nInstance.t('entity.vehicle.labels.batteryCondition'),
    actualBatteryRange: i18nInstance.t('entity.vehicle.labels.actualBatteryRange'),
  }) satisfies FieldMap;

const getSuffixesMap = (i18nInstance: typeof i18n) =>
  ({
    power: i18nInstance.t('general.metric.kW'),
    maximalPower: i18nInstance.t('general.metric.kW'),
    engineVolume: i18nInstance.t('general.metric.ccm'),
    // NEDC fuel consumption fields
    fuelConsumptionUrban: i18nInstance.t('general.metric.l100km'),
    fuelConsumptionCombined: i18nInstance.t('general.metric.l100km'),
    fuelConsumptionExtraUrban: i18nInstance.t('general.metric.l100km'),
    // WLTP fuel consumption fields (liquid/gas)
    fuelConsumptionWltpCombined: i18nInstance.t('general.metric.l100km'),
    fuelConsumptionWltpCityCenter: i18nInstance.t('general.metric.l100km'),
    fuelConsumptionWltpOutskirts: i18nInstance.t('general.metric.l100km'),
    fuelConsumptionWltpCountryRoads: i18nInstance.t('general.metric.l100km'),
    fuelConsumptionWltpHighway: i18nInstance.t('general.metric.l100km'),
    fuelConsumptionWltpWeighted: i18nInstance.t('general.metric.l100km'),
    // WLTP fuel consumption fields (electric)
    fuelConsumptionWltpCombinedKwh: i18nInstance.t('general.metric.kwh100km'),
    fuelConsumptionWltpCityCenterKwh: i18nInstance.t('general.metric.kwh100km'),
    fuelConsumptionWltpOutskirtsKwh: i18nInstance.t('general.metric.kwh100km'),
    fuelConsumptionWltpCountryRoadsKwh: i18nInstance.t('general.metric.kwh100km'),
    fuelConsumptionWltpHighwayKwh: i18nInstance.t('general.metric.kwh100km'),
    fuelConsumptionWltpWeightedCombinedKwh: i18nInstance.t('general.metric.kwh100km'),
    // CO2 emission fields
    carbonDioxideEmission: i18nInstance.t('general.metric.gkm'),
    carbonDioxideEmissionDischarged: i18nInstance.t('general.metric.gkm'),
    carbonDioxideEmissionWeightedCombined: i18nInstance.t('general.metric.gkm'),
    maxTorque: i18nInstance.t('general.metric.Nm'),
    'dimensions.width': i18nInstance.t('general.metric.mm'),
    'dimensions.height': i18nInstance.t('general.metric.mm'),
    'dimensions.length': i18nInstance.t('general.metric.mm'),
    operatingWeight: i18nInstance.t('general.metric.kg'),
    weight: i18nInstance.t('general.metric.kg'),
    maximalLoadCapacity: i18nInstance.t('general.metric.kg'),
    trunkSpace: i18nInstance.t('general.metric.l'),
    wheelBase: i18nInstance.t('general.metric.mm'),
    lastServiceMileage: i18nInstance.t('general.metric.km'),
    nextServiceMileage: i18nInstance.t('general.metric.km'),
    mileage: i18nInstance.t('general.metric.km'),
    warrantyMileage: i18nInstance.t('general.metric.km'),
    serviceContractMileageLimit: i18nInstance.t('general.metric.km'),
    'dimensionsLoadingSpace.length': i18nInstance.t('general.metric.mm'),
    'dimensionsLoadingSpace.width': i18nInstance.t('general.metric.mm'),
    'dimensionsLoadingSpace.height': i18nInstance.t('general.metric.mm'),
    loadingCapacity: i18nInstance.t('general.metric.kg'),
    loadingArea: i18nInstance.t('general.metric.m3'),
    batteryCondition: '%',
    actualBatteryRange: i18nInstance.t('general.metric.km'),
  }) satisfies FieldMap;

const hasStepperList = ['ownerCount', 'primaryKeyCount'] as const;

const minStepperValueMap = {
  ownerCount: 0,
  primaryKeyCount: 0,
} as const;

// Fields that are allowed to have decimal places
const DECIMAL_ALLOWED_FIELDS = new Set([
  // NEDC fuel consumption fields
  'fuelConsumptionUrban',
  'fuelConsumptionCombined',
  'fuelConsumptionExtraUrban',
  // WLTP fuel consumption fields (liquid/gas)
  'fuelConsumptionWltpCombined',
  'fuelConsumptionWltpCityCenter',
  'fuelConsumptionWltpOutskirts',
  'fuelConsumptionWltpCountryRoads',
  'fuelConsumptionWltpHighway',
  'fuelConsumptionWltpWeighted',
  // WLTP fuel consumption fields (electric)
  'fuelConsumptionWltpCombinedKwh',
  'fuelConsumptionWltpCityCenterKwh',
  'fuelConsumptionWltpOutskirtsKwh',
  'fuelConsumptionWltpCountryRoadsKwh',
  'fuelConsumptionWltpHighwayKwh',
  'fuelConsumptionWltpWeightedCombinedKwh',
  // CO2 emission fields
  'carbonDioxideEmission',
  'carbonDioxideEmissionDischarged',
  'carbonDioxideEmissionWeightedCombined',
  // Dimension fields
  'dimensions.width',
  'dimensions.height',
  'dimensions.length',
  'dimensionsLoadingSpace.width',
  'dimensionsLoadingSpace.height',
  'dimensionsLoadingSpace.length',
  'loadingArea',
] as const);

interface SimpleNumericFieldProps {
  name: keyof ReturnType<typeof getSimpleNumericFieldsMap>;
  decimalPlaces?: number;
  forceDecimalPlaces?: boolean;
  suffix?: string;
}

export function SimpleNumericField({
  name,
  decimalPlaces = 2,
  forceDecimalPlaces,
  suffix,
}: SimpleNumericFieldProps) {
  const labels = getSimpleNumericFieldsMap(i18n);
  const suffixes = getSuffixesMap(i18n);

  // Only allow decimal places for whitelisted fields
  const shouldAllowDecimals = DECIMAL_ALLOWED_FIELDS.has(name);
  const finalDecimalPlaces = shouldAllowDecimals ? decimalPlaces : 0;
  const finalForceDecimalPlaces = shouldAllowDecimals ? forceDecimalPlaces : false;

  // Use provided suffix or fall back to map
  const finalSuffix = suffix ?? propOr(undefined, name, suffixes);

  return (
    <Field
      type="number"
      isStepperVisible={hasStepperList.includes(name)}
      name={name}
      decimalPlaces={finalDecimalPlaces}
      forceDecimalPlaces={finalForceDecimalPlaces}
      minStepperValue={propOr(undefined, name, minStepperValueMap)}
      label={prop(name, labels)}
      suffix={finalSuffix}
    />
  );
}
